import React from 'react';
import type { Queue } from '../../types';
import Tooltip from '../common/Tooltip';
import {
  triggerSummaryPills,
  formatTriggerRelativeLine,
  formatTriggerTooltip,
  PILL_TONE_CLASSES,
} from '../../utils/queueTrigger';

interface TriggerSummaryProps {
  queue: Queue;
  /**
   * Optional "now" override for deterministic tests. Defaults to new Date().
   */
  now?: Date;
}

/**
 * Compact trigger-configuration summary for the Queues list (AC-1313).
 *
 * Renders:
 *   - A row of small pills describing interval / thresholds / drain /
 *     retention (or a single "Off" pill when auto-trigger is disabled).
 *   - A muted secondary line with the next (or last) fire relative time.
 *   - A full-detail tooltip on the whole cell.
 */
const TriggerSummary: React.FC<TriggerSummaryProps> = ({ queue, now }) => {
  const pills = triggerSummaryPills(queue);
  const relative = formatTriggerRelativeLine(queue, now);
  const tooltip = formatTriggerTooltip(queue);

  return (
    <Tooltip content={<pre className="text-xs whitespace-pre leading-tight">{tooltip}</pre>}>
      <div className="flex flex-col gap-1" data-testid="trigger-summary">
        <div className="flex flex-wrap items-center gap-1">
          {pills.map((pill, idx) => (
            <span
              key={idx}
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${PILL_TONE_CLASSES[pill.tone]}`}
              title={pill.tooltip}
            >
              {pill.label}
            </span>
          ))}
        </div>
        {relative && (
          <div className="text-[11px] text-gray-500 dark:text-gray-400">{relative}</div>
        )}
      </div>
    </Tooltip>
  );
};

export default TriggerSummary;
